import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as requests from '../requests/index';
import { BaseApiService } from './base-api.service';
import { PlanogramStateList } from '../models/planogram-state-list';
import { PlanogramLevel } from '../models/planogram-level';
import { PlanogramStatusType } from '../models/planogram-status';
import { PlanogramOptions } from '../models/planogram-options';
import { ProductReference } from '../models/layout/product-reference';
import { ToastService } from './toasts.service';
import { RangingDataSearchEntry } from '../models/ranging-data-search-entry';
import { PlanogramService } from './planogram.service';
import { SignalRService } from './signalr.service';
import { ProductOverrideService } from './product-override.service';
import { ApiService } from './api.service';
import { LayoutService } from './layout.service';
import { FileDownloadService } from './file-download.service';
import {
  ClonePlanogramRequest,
  CreatePlanogramRequest,
  RefreshPlanogramRequest
} from '../requests/index';
import { FolderPlanogram } from '../models/folder';
import {
  DeletePlanogramResult,
  CreateReferencePlanogramRequest,
  CreateReferencePlanogramResult,
} from '../models/reference-planogram';
import { RefreshProductsResponse } from '../models/refresh-products';
import { ApprovedPlanogramDTO } from '../models/approved-planogram';
import { BulkClonePlanogramRequest, BulkClonePlanogramResult } from '../requests/bulk-clone-planogram.request';
import { ExportCombinedPlanogramRequest } from '../models/combined-planogram';

@Injectable({
  providedIn: 'root',
})
export class PlanogramApiService extends BaseApiService {
  constructor(
    http: HttpClient,
    toastService: ToastService,
    private layoutService: LayoutService,
    private planogramService: PlanogramService,
    private apiService: ApiService,
    private productOverrideService: ProductOverrideService,
    private signalRService: SignalRService,
    private fileDownloadService: FileDownloadService,
  ) {
    super(http, toastService);
  }

  async refreshPlanogram(
    request: RefreshPlanogramRequest
  ): Promise<PlanogramOptions> {
  return firstValueFrom(
    this.apiRequest<PlanogramOptions>(
      'planogram/refresh-planogram',
      'POST',
      request,
      'Failed to refresh planogram',
    ),
  );
}

  getPlanogramAvailableData(
    storeGuid: string,
    merchId: number,
  ): Observable<RangingDataSearchEntry[] | null> {
    return this.apiRequest<RangingDataSearchEntry[]>(
      `planogram/get-available-data`,
      'POST',
      new requests.SearchRangeDataRequest(storeGuid, merchId),
      'Available planogram data failed to be retrieved.',
    ).pipe(catchError(() => of(null)));
  }

  updateMerchDefaultOrientations(
    merchId: number,
    productOrientations: Map<number, number>,
  ): Observable<boolean> {
    const request = new requests.UpdateMerchDefaultOrientationsRequest(
      merchId,
      productOrientations,
    );
    return this.apiRequest<void>(
      'planogram/update-merch-default-orientations',
      'POST',
      request,
      'Failed to update merchandise default orientations',
    ).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  updatePlanogramSettings(): Observable<boolean> {
    const id = this.planogramService.getPlanogram();
    if (!id) return of(false);
    const stateId = this.planogramService.getPlanogramState();

    const levels = this.planogramService.getLevels();
    const settingsRef = this.planogramService.getSettings();
    const settings: any = {};
    Array.from(settingsRef.keys()).forEach((key) => {
      let data = settingsRef.get(key);
      if (typeof data == 'object') {
        data = JSON.stringify(data);
      } else if (typeof data != 'string') {
        data = `${data}`;
      }
      settings[key] = data;
    });

    return this.apiRequest<void>(
      'planogram/update-settings',
      'POST',
      new requests.UpdateSettingsRequest(id, stateId, levels, settings),
      'Failed to update planogram settings',
    ).pipe(
      map(() => true),
      catchError((err, caught) => {
        this.toastService.show(err.error, {
          classname: `bg-dark text-danger p-3 fs-5`,
          delay: 2000,
        });
        return of(false); // Return an Observable of false instead of just false
      }),
    );
  }

  updateMerchDefaultLevels(
    merchId: number,
    levels: PlanogramLevel[],
  ): Observable<boolean> {
    return this.apiRequest<void>(
      'planogram/update-merch-default-levels',
      'POST',
      new requests.UpdateMerchDefaultLevelsRequest(merchId, levels),
      'Failed to update merchandise default levels',
    ).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  flagProductCaptureRequired(productId: number): Observable<boolean> {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId) return of(false);

    const request: requests.FlagProductCaptureRequiredRequest = {
      planogramId : planogramId,
      stateId : this.planogramService.getPlanogramState(),
      productId : productId
    };

    return this.apiRequest<void>(
      'planogram/flag-product-capture-required',
      'POST',
      request,
      'Failed failed to be flagged for capture required',
    ).pipe(
      map(() => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  getPlanogramStates(): Observable<PlanogramStateList | null> {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId) return of(null);

    return this.apiRequest<PlanogramStateList>(
      'planogram/get-planogram-states',
      'POST',
      new requests.GetPlanogramStatesRequest(planogramId),
      'Planogram states failed to be retrieved',
    ).pipe(catchError(() => of(null)));
  }

  savePlanogram(
    stateId: string | null,
    name: string | null,
    newSave: boolean,
  ): Observable<string | null> {
    const planogramId = this.planogramService.getPlanogram();
    if (!planogramId) return of(null);

    const levels = this.planogramService.getLevels();
    const overrides = this.productOverrideService.getOverrides();
    const settings = this.planogramService.buildSettingsPayload();

    const layout = this.layoutService.getCurrentLayout(false);

    const request = new requests.SavePlanogramStateRequest(
      planogramId,
      stateId,
      newSave,
      name,
      levels,
      overrides,
      settings,
      layout,
    );

    return this.apiRequest<string>(
      'planogram/save-planogram-state',
      'POST',
      request,
      'Planogram state failed to be saved',
    ).pipe(
      tap((newStateId) => {
        this.toastService.show('Planogram state was successfully saved.', {
          classname: 'bg-dark text-success p-3 fs-5',
          delay: 5000,
        });
      }),
      catchError(() => of(null)),
    );
  }

  async loadPlanogram(
    planogramId: string,
    stateId: string | null = null,
  ): Promise<PlanogramOptions> {
    return firstValueFrom(
      this.apiRequest<PlanogramOptions>(
        'planogram/load',
        'POST',
        new requests.LoadPlanogramRequest(planogramId, stateId),
        'Failed to load planogram',
      ),
    );
  }

  exportPlanogram(
    exportType: number,
    exportDescription: string,
    additionalData: Record<string, unknown>,
  ) {
    const planogramState =
      this.planogramService.currentStatus?.state ??
      PlanogramStatusType.INITIALIZING;
    if (planogramState !== PlanogramStatusType.READY) {
      return;
    }

    const layout = this.layoutService.getCurrentLayout(false);

    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId) return;

    const stringifiedAdditionalData: Record<string, string> = {};
    for (const [key, value] of Object.entries(additionalData ?? {})) {
      stringifiedAdditionalData[key] = String(value);
    }

    const payload = new requests.ExportPlanogramRequest(
      planogramId,
      stateId,
      exportType,
      stringifiedAdditionalData,
      layout,
    );
    const errorMsg = `Failed exporting planogram ${exportDescription}`;
    const successMsg = `Downloading planogram ${exportDescription}`;

    this.apiService
      .downloadFile(`${this.apiUrl}/export/export-planogram`, payload)
      .pipe(
        tap((response) => {
          if (!response.body || !response.headers) {
            throw new Error('Invalid response');
          }
          const filename = this.fileDownloadService.filenameFromHeaders(response.headers);
          if (!filename) throw new Error('No filename in response');

          this.fileDownloadService.triggerDownload(response.body, filename);
          this.toastService.show(successMsg, {
            classname: 'bg-dark text-success p-3 fs-5',
            delay: 5000,
          });
        }),
        catchError((error) => {
          this.toastService.show(errorMsg, {
            classname: 'bg-danger text-light p-3 fs-5',
            delay: 5000,
          });
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  /** Lists the combinable planograms under a store (for the Combined Planograms PDF picker). */
  getPlanogramsByStore(storeGuid: string): Promise<FolderPlanogram[]> {
    return firstValueFrom(
      this.apiRequest<FolderPlanogram[]>(
        `planogram/by-store/${storeGuid}`,
        'GET',
        undefined,
        'Failed to load planograms for this store',
      ),
    );
  }

  /** Exports several planograms of one store stitched into a single PDF, then triggers a download. */
  async exportCombinedPlanogram(request: ExportCombinedPlanogramRequest): Promise<void> {
    const errorMsg = 'Failed to export combined planogram';
    const successMsg = 'Downloading combined planogram PDF';
    await firstValueFrom(
      this.apiService
        .downloadFile(`${this.apiUrl}/export/combined-planogram`, request)
        .pipe(
          tap((response) => {
            if (!response.body || !response.headers) {
              throw new Error('Invalid response');
            }
            const filename =
              this.fileDownloadService.filenameFromHeaders(response.headers) ?? 'combined-planogram.pdf';
            this.fileDownloadService.triggerDownload(response.body, filename);
            this.toastService.show(successMsg, {
              classname: 'bg-dark text-success p-3 fs-5',
              delay: 5000,
            });
          }),
          catchError((error) => {
            this.toastService.show(errorMsg, {
              classname: 'bg-danger text-light p-3 fs-5',
              delay: 5000,
            });
            return throwError(() => error);
          }),
        ),
    );
  }

  approvePlanogram(unapproveOthers: boolean = false): Observable<boolean> {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();

    if (!planogramId || !stateId) {
      this.toastService.show('Planogram ID or State ID is missing', {
        classname: `bg-danger text-light p-3 fs-5`,
        delay: 3000,
      });
      return of(false);
    }

    const request = new requests.ApprovePlanogramRequest(planogramId, stateId, unapproveOthers);

    return this.apiRequest<void>(
      'planogram/approve-planogram',
      'POST',
      request,
      'Failed to approve planogram',
    ).pipe(
      map(() => {
        return true;
      }),
      catchError(() => of(false)),
    );
  }

  findOtherApprovedPlanograms(): Observable<ApprovedPlanogramDTO[]> {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId || !stateId) return of([]);

    return this.apiRequest<ApprovedPlanogramDTO[]>(
      'planogram/find-other-approved-planograms',
      'POST',
      new requests.FindOtherApprovedPlanogramsRequest(planogramId, stateId),
      'Failed to find other approved planograms'
    ).pipe(catchError(() => of([])));
  }

  async removeProducts(barcodes: string[]): Promise<boolean> {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId) throw new Error('No planogram selected');
    if (!stateId) throw new Error('No planogram save selected');

    return firstValueFrom(
      this.apiRequest<void>(
        'planogram/remove-dataload-products',
        'POST',
        new requests.RemoveProductsRequest(planogramId, stateId, barcodes),
        'Failed to remove products',
      ).pipe(
        map(() => true),
        catchError(() => of(false)),
      ),
    );
  }

  async importProducts(products: string[]): Promise<ProductReference[]> {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId) throw new Error('No planogram selected');
    if (!stateId) throw new Error('No planogram save selected');

    return firstValueFrom(
      this.apiRequest<ProductReference[]>(
        'planogram/import-dataload-products',
        'POST',
        new requests.ImportProductsRequest(
          planogramId,
          stateId,
          products,
        ),
        'Failed to import products',
      ),
    );
  }

  async refreshProducts(fields: string[]): Promise<RefreshProductsResponse> {
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState();
    if (!planogramId) throw new Error('No planogram selected');
    if (!stateId) throw new Error('No planogram save selected');

    return firstValueFrom(
      this.apiRequest<RefreshProductsResponse>(
        'planogram/refresh-dataload-products',
        'POST',
        new requests.RefreshProductsRequest(
          planogramId,
          stateId,
          fields,
        ),
        'Failed to refresh products',
      ),
    );
  }

  async createPlanogram(
    request: CreatePlanogramRequest,
  ): Promise<FolderPlanogram> {
    return firstValueFrom(
      this.apiRequest<FolderPlanogram>(
        'planogram/create',
        'POST',
        request,
        'Planogram failed to be created',
      ),
    );
  }

  async clonePlanogram(
    request: ClonePlanogramRequest,
  ): Promise<FolderPlanogram> {
    return firstValueFrom(
      this.apiRequest<FolderPlanogram>(
        'planogram/clone-planogram',
        'POST',
        request,
        'Planogram failed to be created',
      ),
    );
  }

  async bulkClonePlanogram(
    request: BulkClonePlanogramRequest,
  ): Promise<BulkClonePlanogramResult> {
    return firstValueFrom(
      this.apiRequest<BulkClonePlanogramResult>(
        'planogram/bulk-clone-planogram',
        'POST',
        request,
        'Planograms failed to be cloned',
      ),
    );
  }

  async deletePlanogram(planogramId: string, confirm: boolean = false): Promise<DeletePlanogramResult> {
    return firstValueFrom(
      this.apiRequest<DeletePlanogramResult>(
        'planogram/delete',
        'POST',
        { planogramId, confirm },
        'Planogram failed to be deleted',
      ),
    );
  }

  async createReferencePlanogram(
    request: CreateReferencePlanogramRequest,
  ): Promise<CreateReferencePlanogramResult> {
    return firstValueFrom(
      this.apiRequest<CreateReferencePlanogramResult>(
        'planogram/create-reference-planogram',
        'POST',
        request,
        'Failed to create linked planograms',
      ),
    );
  }

  async unlinkPlanogram(planogramId: string): Promise<FolderPlanogram> {
    return firstValueFrom(
      this.apiRequest<FolderPlanogram>(
        'planogram/unlink-planogram',
        'POST',
        { planogramId },
        'Failed to unlink planogram',
      ),
    );
  }

  downloadQrCode(): void {
    const storeId = this.planogramService.getStoreGuid();
    if (!storeId) return;

    this.http
      .get(`${this.apiUrl}/planogram/download-qr-code/${storeId}`, {
        responseType: 'blob',
        withCredentials: true,
      })
      .subscribe({
        next: (blob: Blob) =>
          this.fileDownloadService.triggerDownload(blob, `qrcode-${storeId}.png`),
        error: (err) => this.handleError(err, 'Failed to download QR code'),
      });
  }

  isLoading(): boolean {
    return this.signalRService.isLoading();
  }
}
